/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { AntdDatePicker } from 'components/Ui/AntdPickerWrapper'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import clsx from 'clsx'
import { DateRange } from 'types/entities/DateRange'
import UiFilterLabel from 'components/Ui/Filter/UiFilterLabel'
import TableFilterRemoveButton from 'components/Ui/Tanstack/Filters/components/TableFilterRemoveButton'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { Popover, Transition } from '@headlessui/react'
import { RangeFilterLayout } from 'components/Ui/rangeFilterClassNames'
import UiDateRangeCalendar from 'components/Ui/UiDateRangeCalendar'
import { formatDateRangeLabel, formatFilterDate, parseFilterDate } from 'utils/dateRangePresets'

interface UiDateRangePickerProps {
    label?: string
    className?: string
    selectedDay: DateRange | null
    onChange: (date: DateRange | null) => void
    format?: string
    disabled?: boolean
    layout?: RangeFilterLayout
}

const toApiDate = (date: Dayjs) => formatFilterDate(date)

const UiDateRangePicker = (props: UiDateRangePickerProps) => {
    const { t } = useTranslation()
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const layout = props.layout ?? 'inline'

    const [draftSelectedDate, setDraftSelectedDate] = useState<[Dayjs, Dayjs] | null>(null)
    const [isFocused, setIsFocused] = useState(false)

    const selectedDayFrom = props.selectedDay?.from ?? null
    const selectedDayTo = props.selectedDay?.to ?? null

    const selectedDateFromProps = useMemo<[Dayjs, Dayjs] | null>(() => {
        const fromDate = parseFilterDate(selectedDayFrom)
        const toDate = parseFilterDate(selectedDayTo)
        if (!fromDate || !toDate) {
            return null
        }

        return [fromDate, toDate]
    }, [selectedDayFrom, selectedDayTo])

    const selectedDate = draftSelectedDate ?? selectedDateFromProps

    const isSameRange = (firstRange: [Dayjs, Dayjs] | null, secondRange: [Dayjs, Dayjs] | null) => {
        if (!firstRange && !secondRange) {
            return true
        }
        if (!firstRange || !secondRange) {
            return false
        }

        return firstRange[0].isSame(secondRange[0], 'day') && firstRange[1].isSame(secondRange[1], 'day')
    }

    const emitRangeChange = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
        if (!fromDate && !toDate) {
            props.onChange(null)
            return
        }

        const resolvedFrom = fromDate ?? toDate
        const resolvedTo = toDate ?? fromDate

        if (!resolvedFrom || !resolvedTo) {
            return
        }

        props.onChange({
            from: toApiDate(resolvedFrom),
            to: toApiDate(resolvedTo),
        })
    }

    const debouncedRangeChange = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            emitRangeChange(fromDate, toDate)
            setDraftSelectedDate(null)
        }, 300)
    }

    const stackedDateHandler = (side: 'from' | 'to', date: Dayjs | null) => {
        const currentFrom = selectedDate?.[0] ?? null
        const currentTo = selectedDate?.[1] ?? null
        const nextFrom = side === 'from' ? date : currentFrom
        const nextTo = side === 'to' ? date : currentTo

        if (!nextFrom && !nextTo) {
            setDraftSelectedDate(null)
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
            props.onChange(null)
            return
        }

        if (nextFrom && nextTo) {
            const newSelectedDate: [Dayjs, Dayjs] = [nextFrom, nextTo]
            if (isSameRange(selectedDate, newSelectedDate)) {
                return
            }
            setDraftSelectedDate(newSelectedDate)
            debouncedRangeChange(nextFrom, nextTo)
            return
        }

        if (nextFrom) {
            setDraftSelectedDate([nextFrom, nextFrom])
            debouncedRangeChange(nextFrom, nextFrom)
            return
        }

        if (nextTo) {
            setDraftSelectedDate([nextTo, nextTo])
            debouncedRangeChange(nextTo, nextTo)
        }
    }

    const handleCalendarChange = (
        next: { from: Dayjs | null; to: Dayjs | null } | null,
        close?: () => void
    ) => {
        if (!next?.from || !next?.to) {
            setDraftSelectedDate(null)
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
            props.onChange(null)
            return
        }

        const newSelectedDate: [Dayjs, Dayjs] = [next.from, next.to]
        if (isSameRange(selectedDate, newSelectedDate)) {
            close?.()
            return
        }

        setDraftSelectedDate(newSelectedDate)
        emitRangeChange(next.from, next.to)
        setDraftSelectedDate(null)
        close?.()
    }

    const removeFiltersHandler = () => {
        setDraftSelectedDate(null)
        props.onChange(null)
    }

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    const pickerFormat = props.format ? props.format : 'D.M.YYYY'
    const stackedFromValue = selectedDate?.[0] ?? null
    const stackedToValue = selectedDate?.[1] ?? null

    const stackedPickerClassName = clsx(
        'UiDateRangePicker-stacked',
        selectedDate ? '[&_input]:text-cta-600!' : '[&_input]:text-gray-700! dark:[&_input]:text-gray-200!',
        '[&_input]:placeholder:text-gray-400! dark:[&_input]:placeholder:text-gray-500!'
    )

    const triggerLabel = selectedDate
        ? formatDateRangeLabel(selectedDate[0], selectedDate[1])
        : `${t('Date from')} – ${t('Date to')}`

    if (layout === 'stacked') {
        return (
            <div className={clsx('UiDateRangePicker flex flex-col gap-1 w-full min-w-0', props.className)}>
                {props.label && (
                    <div className={clsx('relative flex flex-row items-center')}>
                        <UiFilterLabel label={props.label} />
                        <TableFilterRemoveButton
                            className={clsx('relative! right-auto! mb-2')}
                            inputValue={selectedDate}
                            onClick={removeFiltersHandler}
                        />
                    </div>
                )}
                <div
                    className={clsx(
                        'flex flex-col gap-1 rounded-md border border-gray-300/80 bg-white/50 px-1 py-1 dark:border-gray-700/80 dark:bg-gray-900/50'
                    )}
                >
                    <AntdDatePicker
                        className={stackedPickerClassName}
                        format={pickerFormat}
                        value={stackedFromValue}
                        placeholder={t('Date from')}
                        disabled={props.disabled}
                        allowClear={false}
                        suffixIcon={null}
                        onChange={(date: Dayjs) => stackedDateHandler('from', date)}
                    />
                    <AntdDatePicker
                        className={stackedPickerClassName}
                        format={pickerFormat}
                        value={stackedToValue}
                        placeholder={t('Date to')}
                        disabled={props.disabled}
                        allowClear={false}
                        suffixIcon={null}
                        onChange={(date: Dayjs) => stackedDateHandler('to', date)}
                    />
                </div>
            </div>
        )
    }

    return (
        <div
            className={clsx('UiDateRangePicker flex flex-col w-full min-w-0', props.className)}
            onClick={(e) => e.stopPropagation()}
        >
            {props.label && (
                <div className={clsx('relative flex flex-row items-center')}>
                    <UiFilterLabel label={props.label} />
                    <TableFilterRemoveButton
                        className={clsx('relative! right-auto! mb-2')}
                        inputValue={selectedDate}
                        onClick={removeFiltersHandler}
                    />
                </div>
            )}
            <Popover className="relative mt-1.5 w-full max-w-xs min-w-0">
                {({ close }) => (
                    <>
                        <div
                            className={clsx(
                                'flex flex-row items-center gap-0.5 w-full h-7 relative bg-white/50 border border-gray-300/80 rounded-md transition-all duration-150 focus-within:ring-1 focus-within:ring-cta-light focus-within:bg-white dark:bg-gray-900/50 dark:border-gray-700/80 dark:focus-within:bg-gray-900 font-normal px-1.5 min-w-0',
                                isFocused && 'ring-1 ring-cta-light bg-white dark:bg-gray-900'
                            )}
                        >
                            <Popover.Button
                                disabled={props.disabled}
                                onClick={() => setIsFocused(true)}
                                className={clsx(
                                    'flex-1 min-w-0 truncate bg-transparent text-left text-xs focus:outline-none',
                                    selectedDate
                                        ? 'text-cta-light font-medium'
                                        : 'text-gray-400 dark:text-gray-500'
                                )}
                            >
                                {triggerLabel}
                            </Popover.Button>
                            {selectedDate ? (
                                <button
                                    type="button"
                                    className="shrink-0 p-0.5"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        removeFiltersHandler()
                                    }}
                                    aria-label={t('Clear') as string}
                                >
                                    <XMarkIcon className="w-3.5 h-3.5 text-red-500 hover:text-red-600 transition-colors" />
                                </button>
                            ) : null}
                        </div>

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-150"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                            afterLeave={() => setIsFocused(false)}
                        >
                            <Popover.Panel className="absolute left-0 z-50 mt-2 origin-top-left">
                                <UiDateRangeCalendar
                                    disabled={props.disabled}
                                    value={
                                        selectedDate
                                            ? { from: selectedDate[0], to: selectedDate[1] }
                                            : null
                                    }
                                    onChange={(next) => handleCalendarChange(next, close)}
                                />
                            </Popover.Panel>
                        </Transition>
                    </>
                )}
            </Popover>
        </div>
    )
}

export default UiDateRangePicker
